import * as errors from '../errors'
import { userAuthHeader } from '../apiClient'
import type { IServiceError } from '../errors'
import {
  newRequestId,
  fetchOrThrowNetworkError,
  parseErrorResponse,
} from './utils'
import { HTTPStatus } from '~/api/httpStatus'
import type { TEntityData } from '~/types/models'
import { getPublicRuntimeConfig } from '~/cynqueModules/nuxtAccessors'

export async function httpDelete<T extends TEntityData>({
  callingMn,
  apiPath,
  entityData,
}: {
  callingMn: string
  apiPath: string
  entityData: T
}): Promise<Awaited<void>> {
  const fn = `${callingMn} --> httpDelete |`
  if (!apiPath) {
    throw new Error(`${fn} called with falsey apiPath`)
  }
  if (!entityData) {
    throw new Error(`${fn} called with falsey data`)
  }

  const url = (() => {
    if (Array.isArray(entityData)) {
      if (entityData.length === 1) {
        // Temporary until DELETE [] is implemented. JR.
        return `${apiPath}/${entityData[0].id}`
      } else {
        const queryString =
          '?id=' +
          entityData
            .toReversed() // entityData is in creation order, so parents before children. Delete constraints require flipping.
            .map((item) => item.id)
            .join('&id=')
        return `${apiPath}${queryString}`
      }
    } else if (entityData) {
      return `${apiPath}/${entityData.id}`
    } else {
      return `${apiPath}`
    }
  })()

  console.debug(`${fn} Sending request DELETE ${url}`)
  const headers: Record<string, string> = Object.assign(
    { 'x-request-id': newRequestId() },
    userAuthHeader()
  )
  console.debug(`${fn} Request Id: ${headers['x-request-id']}`)

  // version
  headers['x-app-version'] = getPublicRuntimeConfig().version

  // A transport failure (offline, DNS, reset, CORS, dyno restart) rejects here
  // as a typed NetworkError; HTTP status codes > 300 are NOT network errors and
  // still arrive as a resolved response for the switch below.
  const response = await fetchOrThrowNetworkError(url, {
    method: 'DELETE',
    headers,
  })
  switch (response.status) {
    case HTTPStatus.OK:
    case HTTPStatus.NoContent:
      return

    // A delete blocked by something still referencing the row.
    //
    // ⚠ NOT `UpdateConflictError`, which is what this threw until 2026-09-04.
    // That type's message is "the submitted version is not the stored one", and
    // a DELETE submits no version at all — see the headers built above. The API
    // sends the actual reason as `detail`; `parseErrorResponse` is what gets it
    // into `.message`, where Honeybadger can see it. See DeleteConflictError.
    case HTTPStatus.Conflict: {
      const { message, body } = await parseErrorResponse(response)
      throw new errors.DeleteConflictError('DELETE', url, message, body)
    }
    default: {
      // Was a bare `await response.json()`, which REJECTS on an empty or
      // non-JSON body and would replace the real failure with a SyntaxError.
      const { message, body } = await parseErrorResponse(response)
      throw new errors.APIUnhandledStatus(response.status, {
        statusCode: response.status,
        message,
        body,
      } as unknown as IServiceError)
    }
  }
}
